import _ from 'underscore';
import Radio from 'backbone.radio';
import async from 'async';
import api from '@/lib/api/api';
import session from '@/models/v5/session';
import AccountsCollection from '@/collections/v5/accounts';
import AccountRolesCollection from '@/collections/v5/account_roles';
import GrantsCollection from '@/collections/v5/grants';
import InvitationsCollection from '@/collections/v5/invitations';
import InvitesCollection from '@/collections/v5/invites';
import ProductsCollection from '@/collections/v5/products';
import DevicesCollection from '@/collections/v5/devices';
import DeviceGroupsCollection from '@/collections/v5/device_groups';
import DeploymentsCollection from '@/collections/v5/deployments';
import DeviceGroupProfilesCollection from '@/collections/v5/device_group_profiles';
import DutsCollection from '@/collections/v5/duts';
import WebhooksCollection from '@/collections/v5/webhooks';
import counts_collection from '@/collections/v5/counts';
import LoginKeysCollection from '@/collections/v5/login_keys';
import Notifications from '@/collections/v5/notifications';
import Payments from '@/collections/v5/payment_history';
import Invoices from '@/collections/v5/invoices';

    var collections = [
        AccountsCollection,
        AccountRolesCollection,
        GrantsCollection,
        InvitationsCollection,
        InvitesCollection,
        LoginKeysCollection,
        ProductsCollection,
        DevicesCollection,
        DeviceGroupsCollection,
        DeploymentsCollection,
        DeviceGroupProfilesCollection,
        DutsCollection,
        WebhooksCollection,
        Notifications,
        Payments,
        Invoices
    ];

    var apiCh = Radio.channel('api');

    // Set the singleton global session object for the Api;
    // This is the canonical state for session information like 
    // access_token, expires_at, and _refresh_token.
    //
    // The library requires a session object which contins the token 
    // and refresh tokens.
    api.set_session(session);

    function update_v5_item(collection, item, options) {
        if (!item) {
            return collection;
        } else if (typeof item === 'string') {
            return collection.get(item);
        } else {
            var stored_item = collection.get(item.id);
            if (options && options.remove) {
                return collection.remove(stored_item);
            }
            if (stored_item) {
                stored_item.set(item);
            } else {
                stored_item = collection.add(item);
            }
            return stored_item;
        }
    }
    function apply_api_collection_event_handlers(collection){
        // The multiple types a collection will hold
        // Some collections (eg. the devicegroups collection) will hold
        // multiple types. The convention we use is that if a collection has
        // a TYPES property, it is an array which holds the various types
        var types = [collection.model.type];
        if (collection.types) {
            collection.types.forEach(function(type){
                types.push(type);
            });
        }
        _.compact(types).forEach(function(type) {
            apiCh.reply(type, _.partial(update_v5_item, collection));
        });
    }

    // Add global lookups for 'counts' collection as well.
    // Counts collection is different than these other collections, because
    // counts aren't true v5/json-api type of things. We still want to be able
    // to get the collection of counts in a similar way to all these other
    // typed collection handlers.
    apiCh.reply('counts', _.partial(update_v5_item, counts_collection));

    /**
     * Lookup strategies for the various types we'll see updated via remote
     * update. Each function takes a callback with the arguments
     * function(error, results). If there is an error in any point of the
     * lookup, it will be returned with that value. Success results will be
     * passed to the results argument.
     * @param String id of the item being looked up
     * @param Function callback function with signature (error, results)
     *
     * The lookup strategy for v5 types is gloriously similar
     */
    function v5_lookup_strategy(type, id, cb) {
        var collection = apiCh.request(type);
        var model = collection.get(id);

        if (model && model.type !== 'device') return cb(null, model);

        if (!model) {
            model = collection.add({ id: id }, { silent: true });
        }
        model.fetch({
            success: function() {
                collection.trigger('add', model, collection, {});
                collection.trigger('update', collection, {});
                return cb(null, model);
            },
            error: function(model, error) {
                let err = error.responseJSON.errors;

                if ( model && model[0] && model[0].status ) {
                    err = model;
                } else if ( error && error[0] && error[0].status ) {
                    err = error;
                }

                if (err[0].status && err[0].status == '401') {
                    apiCh.trigger('invalidSession');
                } else if (err[0].status && err[0].status == '404') {
                    model.destroy();
                }

                return cb("Error fetching " + type + " " + id, null);
            }
        });
    }

    var lookups = {
        account: _.partial(v5_lookup_strategy, 'account'),
        grant: _.partial(v5_lookup_strategy, 'grant'),
        invitation: _.partial(v5_lookup_strategy, 'invitation'),
        invite: _.partial(v5_lookup_strategy, 'invite'),
        login_key: _.partial(v5_lookup_strategy, 'login_key'),
        account_role: _.partial(v5_lookup_strategy, 'account_role'),
        account_metrics: _.partial(v5_lookup_strategy, 'account_metrics'),

        product: _.partial(v5_lookup_strategy, 'product'),
        development_devicegroup: _.partial(v5_lookup_strategy, 'development_devicegroup'),
        production_devicegroup: _.partial(v5_lookup_strategy, 'production_devicegroup'),
        dut_devicegroup: _.partial(v5_lookup_strategy, 'dut_devicegroup'),
        factoryfixture_devicegroup: _.partial(v5_lookup_strategy, 'factoryfixture_devicegroup'),
        pre_production_devicegroup: _.partial(v5_lookup_strategy, 'pre_production_devicegroup'),
        pre_dut_devicegroup: _.partial(v5_lookup_strategy, 'pre_dut_devicegroup'),
        pre_factoryfixture_devicegroup: _.partial(v5_lookup_strategy, 'pre_factoryfixture_devicegroup'),
        deployment: _.partial(v5_lookup_strategy, 'deployment'),        
        notification: _.partial(v5_lookup_strategy, 'notification'),

        device: _.partial(v5_lookup_strategy, 'device')
    };

    /**
     * When remote update events come in (probably from a websocket, but we've
     * already abstracted it at this point) they should look like this:
     *  {
     *      data: { id: <some-id>, type: <some-type> [, attributes: <attrs>] },
     *      meta: {
     *          created_by: <id-of-account-that-initiated-the-update>,
     *          owner_id: <id-of-account-which-owns-resource>
     *          action: <action>
     *      }
     *  }
     * Where 'action' is one of the following: create, update, delete.
     * We need to turn all of these things into objects the app understands,
     * fetching things as necessary along the way.
     */
    function handle_remote_update_events(event) {
        var data = event.data;
        var meta = event.meta;
        var action = meta.action;
        var type = data.type;
        var id = data.id;

        // Handler for delete events
        function delete_model(callback) {
            var model = apiCh.request(type, id);

            if (model && model.collection) {
                model.collection.remove(model);
            }

            return callback(null, model);
        }
        // Hander for create/update events
        function lookup_model(callback) {
            var lookup = lookups[type];

            if (!lookup) {
                var err = "Couldn't find a lookup strategy for type " + type;
                return callback(err, null);
            }
            lookup(id, callback);
        }
        // On completion of the data fetching, trigger a 'remote' event
        function trigger_event(error, results) {
            if (error) return console.warn(error);
            var model = results.model;
            var who = results.changed_by;
            var owner = results.owner;
            if (model && model.ignore_remote_updates) {
                model.ignore_remote_updates = false;
                return;
            }
            var event_data = {
                action: action,
                model: model,
                who: who,
                owner: owner
            };
            // We only need to fetch in the event of an update. If the event
            // we received was a 'create' event, it got fetched in the context
            // of the lookup. If we got a 'delete' event, we don't want to
            // fetch something which no longer exists.
            if (action == 'update') {
                results.model.fetch({
                    success: function() {
                        apiCh.trigger('remote', event_data);
                    }
                });
                return;
            }
            apiCh.trigger('remote', event_data);
        }
        var tasks = {
            model: action === 'delete' ? delete_model : lookup_model,
            changed_by: _.partial(lookups.account, meta.changed_by),
            owned_by: _.partial(lookups.account, meta.owned_by)
        };
        async.parallel(tasks, trigger_event);
    }
    function get_v5_model_promise(collection, id) {
        var p = new Promise(function(resolve, reject) {
            var model = collection.get(id);

            if (model) {
                return resolve(model);
            }

            model = collection.add({ id: id }, { silent: true });

            return model.fetch()
            .done(function() {
                collection.trigger('add', model, collection, {});
                collection.trigger('update', collection, {});
                resolve(model);
            })
            .fail(function() {
                collection.remove(model);
                reject("Error fetching " + model.type + " " + id);
            });
        });
        return p;
    }
    function get_promise(type, id) {
        var collection = apiCh.request(type);
        return get_v5_model_promise(collection, id);
    }
    export default function(options) {
        var api_options = options.api;
        var origin = api_options && api_options.origin;

        api.setOrigin(origin);

        // Set up Api data handlers
        collections.forEach(apply_api_collection_event_handlers);

        // Set up promises handler (experimental)
        apiCh.reply('promise', get_promise);
        apiCh.reply('fetch_model', get_promise);

        // Set up an event listener to handle remote update events
        apiCh.on('remote_raw', handle_remote_update_events);
    }

